import { AutoComplete, Space, Spin } from 'antd';
import { MapPin } from 'phosphor-react';
import { useCallback, useEffect, useRef, useState } from 'react';

interface LocationAutocompleteProps {
    label: string;
    placeholder: string;
    value: string;
    onChange: (value: string) => void;
    useGoogleMaps?: boolean;
}

interface Prediction {
    description: string;
    place_id: string;
    structured_formatting: {
        main_text: string;
        secondary_text: string;
    };
}

const LocationAutocomplete = ({
    label,
    placeholder,
    value,
    onChange,
    useGoogleMaps = false
}: LocationAutocompleteProps) => {
    const [predictions, setPredictions] = useState<Prediction[]>([]);
    const [isGoogleMapsLoaded, setIsGoogleMapsLoaded] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null);

    // Check if Google Maps is already loaded
    const checkGoogleMapsLoaded = useCallback(() => {
        return !!(
            window.google &&
            window.google.maps &&
            window.google.maps.places &&
            window.google.maps.places.AutocompleteService
        );
    }, []);

    // Wait for Google Maps to be loaded by GoogleMapsLoader
    useEffect(() => {
        if (!useGoogleMaps) return;

        // If already loaded, set state
        if (checkGoogleMapsLoaded()) {
            setIsGoogleMapsLoaded(true);
            return;
        }

        // Wait for Google Maps to be loaded
        const checkInterval = setInterval(() => {
            if (checkGoogleMapsLoaded()) {
                setIsGoogleMapsLoaded(true);
                setError(null);
                clearInterval(checkInterval);
            }
        }, 100);

        // Timeout after 10 seconds
        const timeout = setTimeout(() => {
            clearInterval(checkInterval);
            if (!checkGoogleMapsLoaded()) {
                setError(
                    'Google Maps API not available. Please ensure GoogleMapsLoader is properly configured.'
                );
            }
        }, 10000);

        return () => {
            clearInterval(checkInterval);
            clearTimeout(timeout);
        };
    }, [useGoogleMaps, checkGoogleMapsLoaded]);

    const performGoogleMapsSearch = useCallback(
        async (searchValue: string) => {
            if (!checkGoogleMapsLoaded()) {
                throw new Error('Google Maps API not available');
            }

            return new Promise<Prediction[]>((resolve, reject) => {
                try {
                    const service =
                        new window.google.maps.places.AutocompleteService();
                    const request = {
                        input: searchValue,
                        types: ['geocode', 'establishment']
                    };

                    service.getPlacePredictions(
                        request,
                        (predictions, status) => {
                            if (
                                status ===
                                    window.google.maps.places
                                        .PlacesServiceStatus.OK &&
                                predictions
                            ) {
                                resolve(predictions as Prediction[]);
                            } else if (
                                status ===
                                window.google.maps.places.PlacesServiceStatus
                                    .ZERO_RESULTS
                            ) {
                                resolve([]);
                            } else {
                                reject(
                                    new Error(
                                        `Google Maps API error: ${status}`
                                    )
                                );
                            }
                        }
                    );
                } catch (err) {
                    reject(new Error('Failed to create AutocompleteService'));
                }
            });
        },
        [checkGoogleMapsLoaded]
    );

    const performSearch = useCallback(
        async (searchValue: string) => {
            if (searchValue.length < 2) {
                setPredictions([]);
                setIsLoading(false);
                return;
            }

            setIsLoading(true);
            setError(null);

            try {
                if (useGoogleMaps && isGoogleMapsLoaded) {
                    const results = await performGoogleMapsSearch(searchValue);
                    setPredictions(results);
                } else {
                    setPredictions([]);
                }
            } catch (err) {
                console.error('Search error:', err);
                setError(err instanceof Error ? err.message : 'Search failed');
                setPredictions([]);
            } finally {
                setIsLoading(false);
            }
        },
        [useGoogleMaps, isGoogleMapsLoaded, performGoogleMapsSearch]
    );

    const debouncedSearch = useCallback(
        (searchValue: string) => {
            if (debounceTimeoutRef.current) {
                clearTimeout(debounceTimeoutRef.current);
            }

            debounceTimeoutRef.current = setTimeout(() => {
                performSearch(searchValue);
            }, 300);
        },
        [performSearch]
    );

    useEffect(() => {
        return () => {
            if (debounceTimeoutRef.current) {
                clearTimeout(debounceTimeoutRef.current);
            }
        };
    }, []);

    const handleChange = (newValue: string) => {
        onChange(newValue);

        if (newValue.length >= 2) {
            debouncedSearch(newValue);
        } else {
            setPredictions([]);
            setError(null);
            setIsLoading(false);
        }
    };

    const handleSelect = (selectedValue: string) => {
        onChange(selectedValue);
        setPredictions([]);
        setError(null);
        setIsLoading(false);
    };

    const options = predictions.map((prediction) => ({
        value: prediction.description,
        label: (
            <div>
                <div style={{ fontWeight: 'bold' }}>
                    {prediction.structured_formatting?.main_text ||
                        prediction.description}
                </div>
                {prediction.structured_formatting?.secondary_text && (
                    <div style={{ fontSize: '12px', color: '#666' }}>
                        {prediction.structured_formatting.secondary_text}
                    </div>
                )}
            </div>
        )
    }));

    return (
        <Space direction="vertical" style={{ width: '100%', minWidth: 0 }}>
            <label>{label}</label>
            <div className="custom-select">
                <div className="cus-icon">
                    <MapPin size={20} weight="bold" />
                </div>
                <AutoComplete
                    placeholder={placeholder}
                    options={options}
                    value={value}
                    onChange={handleChange}
                    onSelect={handleSelect}
                    filterOption={false}
                    notFoundContent={
                        isLoading ? (
                            <Spin size="small" />
                        ) : error ? (
                            <span style={{ color: '#ff4d4f' }}>{error}</span>
                        ) : null
                    }
                    style={{ width: '100%', minWidth: 0, fontSize: 18 }}
                    dropdownStyle={{ zIndex: 1000 }}
                />
            </div>
        </Space>
    );
};

export default LocationAutocomplete;
